<script setup>
/**
 * Displays a scroll‑aware, paginated grid of product cards driven by two Pinia
 * stores (`productListStore` + `filterStore`). Handles:
 *   • Skeleton placeholders while data loads.
 *   • An empty‑result state when filters return zero products.
 *   • Client‑side scrolling when pagination links are clicked (via `scrollTop`).
 *   • Waiting for the Sylius back‑end flag (`syliusLoaded`) before proceeding
 *     with further UI updates to avoid race conditions.
 */
import { computed, onMounted, watch } from 'vue'
import { useProductListStore } from '@/store'
import Card from './ModuleCard.vue'
import Pagination from './ModulePagination.vue'
import { loadLanguageAsync } from '@/plugins/i18n'
import { gsap } from 'gsap'
import {useFilterStore} from "@/store/filter";

// Initialize the store
const store = useProductListStore()
const filterStore = useFilterStore()

/**
 * Reactive array of product items sourced from the Pinia product store.
 */
const items = computed(() => store.items)

/**
 * Signals when filter metadata and product list have finished loading. Used to
 * switch between skeleton and real content.
 */
const loaded = computed(() => filterStore.loaded && store.loaded)

/**
 * `true` when current filter combination yields zero products.
 */
const emptyFilterResult = computed(() => store.isEmptyFilterResult)

/**
 * Smooth‑scroll the viewport to the top of the product list, leaving a 100‑px
 * offset for sticky headers. Triggered by the Pagination component via emit.
 */
const scrollTop = () => {
  const el = document.querySelector('.product__list')
  const domRect = el.getBoundingClientRect()
  const scrollingElement = document.scrollingElement || document.documentElement
  gsap.to(window, { duration: 0.5, scrollTo: { y: domRect.top + scrollingElement.scrollTop - 100, autoKill: false } })
}

/**
 * Resolve after the Sylius back‑end flag flips to `true`, or after a timeout.
 * Prevents UI actions that depend on back‑end availability from firing too
 * early. Returns a promise that always resolves.
 */
function waitForSylius(timeoutMs = 4000) {
  return new Promise(resolve => {
    if (store.syliusLoaded) return resolve()
    const stop = watch(() => store.syliusLoaded, v => {
      if (v) { stop(); resolve() }
    })
    // safety timeout
    setTimeout(() => { stop(); resolve() }, timeoutMs)
  })
}

// Kick off language loading + initial product fetch on mount.
onMounted(() => {
  loadLanguageAsync().then(() => {
    store.setPredefinedFilter()
    store.get()
  })
})

// Watch the `loaded` flag; once true, handle Sylius call and wait for it.
watch(
  loaded,
  async () => {
    if (!loaded.value) {
      return
    }

    setTimeout(async () => {
      store.handleSyliusCall()
      await waitForSylius()
    }, 300)
  },
  { immediate: true }
)
</script>

<template>
  <div class="list__list">
    <div class="product__list">
      <div class="list__list" v-if="loaded">
        <Card v-for="(item, idx) in items" :key="idx" :item="item" />
        <div v-if="emptyFilterResult" class="empty-result-container">
          <span>{{ $t('filterLabels.noResultsFound') || 'For the chosen filter options no matching products were found.' }}</span>
        </div>
        <div class="card__card--placeholder"></div>
      </div>
      <div class="list__list" v-else>
        <template v-for="n in 9" :key="n">
          <div class="card__item card__item--loading">
            <div class="item__image"></div>
            <div class="item__content">
              <div class="content__headline"><span></span><span></span></div>
              <div class="content__meta"><span></span></div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <Pagination v-if="loaded && !emptyFilterResult && store.totalPages > 1" @scrollTop="scrollTop" />
  </div>
</template>
